import React from 'react';
import styles from '../irlObservations2026.module.css';
import { FilterChip, FreshnessBadge, PriorityMark, RagMark, SearchField, StatusBadge } from '../chrome';
import { BulkEditBar, ListCheckbox, ListKebabMenu, ListNotice } from '../listChrome';
import { filledUpdates, toggleRowSelection, visibleSelectionState } from '../listActions';
import { OBS_STATUS_FILTERS } from '../dummyData';
import { observationRagLabel, stripHtmlText } from './shared';

function workstreamLabel(item, workstreams) {
    const match = (workstreams || []).find(ws => String(ws.id) === String(item.workstreamId));
    return match?.short || item.workstream;
}

function commentsLabel(item) {
    if (!item.comments) return '';
    return `${item.comments} comments${item.newComments ? ` · ${item.newComments} new` : ''}`;
}

export default function Obs_Main({
    observations,
    status,
    onStatus,
    workstream,
    onWorkstream,
    materiality,
    onMateriality,
    query,
    onQuery,
    onOpen,
    onCreate,
    onReview,
    workstreams,
    members,
    stats,
    statusFilters,
    menuOpen,
    onMenu,
    selectedIds,
    onSelectedIds,
    bulkDraft,
    onBulkDraft,
    onBulkApply,
    onBulkDelete,
    onExportCsv,
    onExportPdf,
    onDownloadTemplate,
    onImport,
    notice,
    busy,
}) {
    const visibleIds = observations.map(item => item.id);
    const selection = visibleSelectionState(visibleIds, selectedIds);
    const selectedCount = (selectedIds || []).length;

    const toggleVisible = () => {
        const selected = new Set((selectedIds || []).map(id => String(id)));
        if (selection.allVisibleSelected) {
            visibleIds.forEach(id => selected.delete(String(id)));
        } else {
            visibleIds.forEach(id => selected.add(String(id)));
        }
        onSelectedIds(Array.from(selected));
    };

    return (
        <>
            <div className={`${styles.contentHeader} ${styles.obsContentHeader}`}>
                <div>
                    <h2 className={`${styles.contentTitle} ${styles.obsContentTitle}`}>Observations</h2>
                    <p className={styles.contentSubtitle}>Capture and review evidence-backed project observations over time.</p>
                </div>
                <div className={styles.headerActions}>
                    <button type="button" className={styles.warnOutlineBtn} onClick={onReview}>
                        {stats?.mayNeedReview || 0} may need review
                    </button>
                    <button type="button" className={styles.primaryBtn} onClick={onCreate}>
                        + New Observation
                    </button>
                    <ListKebabMenu
                        open={menuOpen}
                        onToggle={onMenu}
                        onExportCsv={onExportCsv}
                        onExportPdf={onExportPdf}
                        onDownloadTemplate={onDownloadTemplate}
                        onImport={onImport}
                        disabled={busy}
                    />
                </div>
            </div>
            <div className={styles.filterRow}>
                <SearchField value={query} onChange={onQuery} placeholder="Search observations…" />
                {(statusFilters || OBS_STATUS_FILTERS).map(item => (
                    <FilterChip key={item} label={item} active={status === item} onClick={() => onStatus(item)} />
                ))}
                <select className={styles.select} value={workstream} onChange={event => onWorkstream(event.target.value)}>
                    <option value="all">All workstreams</option>
                    {(workstreams || []).map(ws => (
                        <option key={ws.id} value={String(ws.id)}>
                            {ws.short || ws.name}
                        </option>
                    ))}
                </select>
                <select className={styles.select} value={materiality} onChange={event => onMateriality(event.target.value)}>
                    <option value="all">Materiality</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                </select>
            </div>
            <ListNotice message={notice} />
            <BulkEditBar
                count={selectedCount}
                busy={busy}
                applyDisabled={!Object.keys(filledUpdates(bulkDraft || {})).length}
                onApply={onBulkApply}
                onDelete={onBulkDelete}
                onClear={() => onSelectedIds([])}
                deleteSingular="observation"
                deletePlural="observations"
                fields={[
                    {
                        key: 'owner',
                        label: 'Assigned to',
                        value: bulkDraft?.owner || '',
                        onChange: value => onBulkDraft({ owner: value }),
                        options: (members || []).map(member => ({ id: member.id, label: member.name })),
                    },
                    {
                        key: 'workstream',
                        label: 'Workstream',
                        value: bulkDraft?.workstream || '',
                        onChange: value => onBulkDraft({ workstream: value }),
                        options: (workstreams || []).map(ws => ({ id: ws.id, label: ws.short || ws.name })),
                    },
                    {
                        key: 'materiality',
                        label: 'Materiality',
                        value: bulkDraft?.materiality || '',
                        onChange: value => onBulkDraft({ materiality: value }),
                        options: [
                            { id: 'High', label: 'High' },
                            { id: 'Medium', label: 'Medium' },
                            { id: 'Low', label: 'Low' },
                        ],
                    },
                ]}
            />
            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th className={styles.checkCol}>
                                <ListCheckbox
                                    appearance="header"
                                    checked={selection.allVisibleSelected}
                                    indeterminate={selection.someVisibleSelected}
                                    onChange={toggleVisible}
                                    label="Select all visible observations"
                                />
                            </th>
                            <th>Observation</th>
                            <th>Workstream</th>
                            <th>Materiality</th>
                            <th>RAG</th>
                            <th>Status</th>
                            <th>Freshness</th>
                            <th>Updated</th>
                            <th>Evidence</th>
                        </tr>
                    </thead>
                    <tbody>
                        {observations.map(item => {
                            const comments = commentsLabel(item);
                            const checked = (selectedIds || []).includes(item.id);
                            return (
                                <tr key={item.id}>
                                    <td className={styles.checkCol}>
                                        <ListCheckbox
                                            checked={checked}
                                            onChange={() => onSelectedIds(toggleRowSelection(selectedIds, item.id))}
                                            label={`Select ${item.id}`}
                                        />
                                    </td>
                                    <td className={styles.obsTitleCell}>
                                        <button type="button" className={styles.rowBtn} onClick={() => onOpen(item.id)}>
                                            <span className={styles.rowId}>{item.id}</span>
                                            <span className={`${styles.rowTitle} ${styles.obsRowTitle}`}>{stripHtmlText(item.title)}</span>
                                        </button>
                                    </td>
                                    <td className={styles.wsMuted}>{workstreamLabel(item, workstreams)}</td>
                                    <td>
                                        <PriorityMark value={item.materiality} />
                                    </td>
                                    <td>
                                        <RagMark value={observationRagLabel(item, { fallback: true })} />
                                    </td>
                                    <td>
                                        <StatusBadge status={item.status} appearance="text" />
                                    </td>
                                    <td>
                                        <FreshnessBadge value={item.freshness} appearance="pill" />
                                    </td>
                                    <td className={styles.wsMuted}>{item.updated}</td>
                                    <td className={styles.evidenceCell}>
                                        <span className={styles.evidenceSources}>{item.sources ?? 0} sources</span>
                                        {comments ? <span className={styles.evidenceComments}>{comments}</span> : null}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
}
